import React, { useState } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Empty,
  Select,
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Popconfirm,
  message,
  Tooltip,
  Drawer,
  Descriptions,
  Divider,
  Alert,
  Progress,
  Radio,
} from "antd";
import {
  DeploymentUnitOutlined,
  TeamOutlined,
  LinkOutlined,
  DisconnectOutlined,
  ExperimentOutlined,
  EyeOutlined,
  BarcodeOutlined,
  MedicineBoxOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  SaveOutlined,
  ThunderboltOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { UnifiedTraceableLogModal, getTraceabilityTerminology } from "@ryo/ui";
import { useCycleTraceability, useTraceableGroups } from "../../hooks/useProductionCycles";
import { useSecureRequest } from "../../hooks/useSecureRequest";

const { Text, Title, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface Props {
  cycle: any;
  tokenData: any;
  companyUid: string;
  companyId: number;
}

interface RowSaveStatus {
  status: "idle" | "saving" | "saved" | "error" | "invalid";
  savedAt?: string;
  errorMsg?: string;
  savedWeight?: number;
}

export const CycleTraceabilityMembers: React.FC<Props> = ({
  cycle,
  tokenData,
  companyUid,
  companyId,
}) => {
  const { securePost } = useSecureRequest();
  const terminology = getTraceabilityTerminology(cycle?.productive_unit_type);

  const { links, isLoading, mutate: mutateLinks } = useCycleTraceability(tokenData, {
    cycle_id: cycle?.id,
  });

  const { groups: availableGroups } = useTraceableGroups(tokenData, {
    company: companyId,
  });

  const [linkModalVisible, setLinkModalVisible] = useState(false);
  const [selectedGroupIdToLink, setSelectedGroupIdToLink] = useState<number | undefined>(undefined);
  const [linkingLoading, setLinkingLoading] = useState(false);

  // Individual Log Modal State
  const [memberLogModalVisible, setMemberLogModalVisible] = useState(false);
  const [selectedMember, setSelectedMember] = useState<any>(null);

  // Member Detail Drawer
  const [memberDrawerVisible, setMemberDrawerVisible] = useState(false);
  const [viewingMember, setViewingMember] = useState<any>(null);

  // Add Member to Group Modal
  const [addMemberModalVisible, setAddMemberModalVisible] = useState(false);
  const [addMemberForm] = Form.useForm();
  const [addMemberLoading, setAddMemberLoading] = useState(false);

  // Batch Weighing Modal State
  const [batchWeighingVisible, setBatchWeighingVisible] = useState(false);
  const [batchWeighingForm] = Form.useForm();
  const [batchWeights, setBatchWeights] = useState<Record<number, number | undefined>>({});
  const [rowStatuses, setRowStatuses] = useState<Record<number, RowSaveStatus>>({});
  const [filterStatus, setFilterStatus] = useState<"ALL" | "PENDING" | "SAVED">("ALL");
  const [searchText, setSearchText] = useState("");
  const [batchLoading, setBatchLoading] = useState(false);

  // Batch Event for Selected Members
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [batchEventVisible, setBatchEventVisible] = useState(false);

  const activeLink = links && links.length > 0 ? links[0] : null;
  const linkedGroup = activeLink?.traceable_instance_groups;
  const members = linkedGroup?.traceable_instance_group_members || [];
  const activeMembers = members.filter((m: any) => m.is_active);

  const handleOpenBatchWeighing = () => {
    const initialMap: Record<number, number | undefined> = {};
    const initialStatuses: Record<number, RowSaveStatus> = {};
    const todayStr = dayjs().format("YYYY-MM-DD");

    activeMembers.forEach((m: any) => {
      const instId = m.instance_id || m.traceable_instances?.id || m.id;
      const currentW = m.traceable_instances?.metadata?.weight_kg;
      const lastWeighedAt = m.traceable_instances?.metadata?.last_weighed_at;

      // If already weighed today, prepopulate with today's saved weight
      if (lastWeighedAt && dayjs(lastWeighedAt).format("YYYY-MM-DD") === todayStr && currentW !== undefined) {
        initialMap[instId] = currentW;
        initialStatuses[instId] = {
          status: "saved",
          savedAt: dayjs(lastWeighedAt).format("HH:mm"),
          savedWeight: currentW,
        };
      } else {
        // Pending animals start with input empty so focusing and unfocusing without editing does NOT trigger false saves
        initialMap[instId] = undefined;
        initialStatuses[instId] = {
          status: "idle",
        };
      }
    });

    setBatchWeights(initialMap);
    setRowStatuses(initialStatuses);
    setSearchText("");
    setFilterStatus("ALL");

    batchWeighingForm.resetFields();
    batchWeighingForm.setFieldsValue({
      log_date: dayjs(),
      title: `${terminology.batchActionLabel}: ${linkedGroup?.name || cycle.name}`,
    });
    setBatchWeighingVisible(true);
  };

  // Instant per-animal save handler with validation of unchanged / empty / zero values
  const handleSaveSingleWeight = async (memberObj: any, explicitWeight?: number) => {
    const instId = memberObj.instance_id || memberObj.traceable_instances?.id || memberObj.id;
    const targetInstanceId = memberObj?.traceable_instances?.id || memberObj?.instance_id || instId;
    const weightToSave = explicitWeight !== undefined ? explicitWeight : batchWeights[instId];

    // Case: Empty, zero, or negative value
    if (weightToSave === undefined || weightToSave === null || weightToSave <= 0) {
      if (weightToSave !== undefined && weightToSave !== null && weightToSave <= 0) {
        setRowStatuses((prev) => ({
          ...prev,
          [instId]: {
            status: "invalid",
            errorMsg: `El ${terminology.metricWeightLabel.toLowerCase()} debe ser mayor a 0`,
          },
        }));
      } else {
        setRowStatuses((prev) => ({
          ...prev,
          [instId]: { status: "idle" },
        }));
      }
      return;
    }

    const currentStatus = rowStatuses[instId];
    // Case: Already saved with exact same value -> skip redundant network request
    if (currentStatus?.status === "saved" && currentStatus.savedWeight === weightToSave) {
      return;
    }

    setRowStatuses((prev) => ({
      ...prev,
      [instId]: { status: "saving" },
    }));

    try {
      const formValues = batchWeighingForm.getFieldsValue();
      const logDate = formValues.log_date ? formValues.log_date.toISOString() : new Date().toISOString();
      const previousWeight = memberObj?.traceable_instances?.metadata?.weight_kg || 0;
      const gainKg = previousWeight > 0 ? Number((weightToSave - previousWeight).toFixed(2)) : 0;

      await securePost('/traceability/create-log', {
        company_uid: companyUid,
        log: {
          instance_id: targetInstanceId,
          log_date: logDate,
          title: formValues.title || `${terminology.batchActionLabel} Colectiva`,
          notes: formValues.notes ? `${terminology.batchActionLabel}: ${formValues.notes}` : `${terminology.batchActionLabel} en ciclo "${cycle.name}"`,
          metrics: {
            weight_kg: weightToSave,
            previous_weight_kg: previousWeight,
            gain_kg: gainKg,
            last_weighed_at: logDate,
            last_weighed_cycle_id: cycle.id,
          },
          reference_type: "CYCLE_MEMBER_LOG",
          reference_id: cycle.id,
        },
        update_instance_metadata: true,
        csrfToken: tokenData?.token?.uid,
      });

      const nowFormatted = dayjs(logDate).format("HH:mm");
      setRowStatuses((prev) => ({
        ...prev,
        [instId]: {
          status: "saved",
          savedAt: nowFormatted,
          savedWeight: weightToSave,
        },
      }));
    } catch (err: any) {
      console.error("Error auto-saving weight for animal:", instId, err);
      setRowStatuses((prev) => ({
        ...prev,
        [instId]: {
          status: "error",
          errorMsg: err.message || "Error al guardar",
        },
      }));
      message.error(`Error guardando ${memberObj.traceable_instances?.identifier || instId}: ${err.message || "Fallo de conexión"}`);
    }
  };

  // Close batch weighing modal and refresh member links
  const handleCloseBatchWeighing = () => {
    setBatchWeighingVisible(false);
    mutateLinks();
  };

  // Finish and close batch weighing modal
  const handleFinishBatchWeighing = async () => {
    try {
      const formValues = await batchWeighingForm.validateFields();
      setBatchLoading(true);

      // Save any pending rows that have entered weights but haven't been saved yet
      for (const m of activeMembers) {
        const instId = m.instance_id || m.traceable_instances?.id || m.id;
        const enteredWeight = batchWeights[instId];
        const statusObj = rowStatuses[instId];
        if (enteredWeight && enteredWeight > 0 && statusObj?.status !== "saved") {
          await handleSaveSingleWeight(m, enteredWeight);
        }
      }

      const savedCount = Object.values(rowStatuses).filter((s) => s.status === "saved").length;
      const logDate = formValues.log_date ? formValues.log_date.toISOString() : new Date().toISOString();

      // Register cycle operational log if labor cost or notes were recorded
      if (formValues.cost || formValues.notes) {
        try {
          await securePost('/manufacturing/create-log', {
            company_uid: companyUid,
            log: {
              cycle_id: cycle.id,
              log_type: "OBSERVATION",
              log_date: logDate,
              notes: `${terminology.batchModalTitle}: ${savedCount} ${terminology.memberUnitLabel.toLowerCase()} procesados. ${formValues.notes || ""}`,
              metrics: {
                weighed_count: savedCount,
                total_labor_cost: formValues.cost || 0,
              },
            },
            csrfToken: tokenData?.token?.uid,
          });
        } catch (cycleErr) {
          console.error("Error creating cycle log for batch action:", cycleErr);
        }
      }

      message.success(`Jornada finalizada: ${savedCount} ${terminology.memberUnitLabel.toLowerCase()} registrados exitosamente`);
      setBatchWeighingVisible(false);
      await mutateLinks();
    } catch (err: any) {
      message.error(err.message || `Error al finalizar ${terminology.batchActionLabel.toLowerCase()}`);
    } finally {
      setBatchLoading(false);
    }
  };

  // --- Batch Event for Selected Members ---
  const selectedInstances = activeMembers.filter((m: any) => selectedRowKeys.includes(m.id));

  const handleOpenBatchEvent = () => {
    setBatchEventVisible(true);
  };

  const handleSaveBatchEvent = async ({ log, targetInstanceIds }: any) => {
    try {
      let countDone = 0;
      for (const instId of (targetInstanceIds || [])) {
        await securePost('/traceability/create-log', {
          company_uid: companyUid,
          log: {
            ...log,
            instance_id: instId,
          },
          update_instance_metadata: true,
          csrfToken: tokenData?.token?.uid,
        });
        countDone++;
      }

      message.success(`Evento registrado en la hoja de vida de ${countDone} miembros seleccionados`);
      setSelectedRowKeys([]);
      mutateLinks();
    } catch (err: any) {
      message.error(err.message || "Error al registrar eventos por lote");
      throw err;
    }
  };

  const handleLinkGroup = async () => {
    if (!selectedGroupIdToLink) return;
    try {
      setLinkingLoading(true);
      const res = await securePost('/traceability/link-cycle-group', {
        cycle_id: cycle.id,
        group_id: selectedGroupIdToLink,
        csrfToken: tokenData?.token?.uid,
      });
      if (res?.error) throw new Error(res.message);
      message.success("Grupo trazable vinculado al ciclo con éxito");
      setLinkModalVisible(false);
      mutateLinks();
    } catch (err: any) {
      message.error(err.message || "Error al vincular el grupo");
    } finally {
      setLinkingLoading(false);
    }
  };

  const handleUnlinkGroup = async () => {
    if (!linkedGroup) return;
    try {
      const res = await securePost('/traceability/unlink-cycle-group', {
        cycle_id: cycle.id,
        group_id: linkedGroup.id,
        csrfToken: tokenData?.token?.uid,
      });
      if (res?.error) throw new Error(res.message);
      message.success("Grupo desvinculado del ciclo");
      mutateLinks();
    } catch (err: any) {
      message.error(err.message || "Error al desvincular");
    }
  };

  const handleOpenMemberLog = (memberInstance: any) => {
    setSelectedMember(memberInstance);
    setMemberLogModalVisible(true);
  };

  const handleSaveMemberLog = async ({ log, update_instance_status, update_instance_metadata }: any) => {
    try {
      const res = await securePost('/traceability/create-log', {
        company_uid: companyUid,
        log: {
          ...log,
          instance_id: selectedMember.id,
        },
        update_instance_status,
        update_instance_metadata,
        csrfToken: tokenData?.token?.uid,
      });

      if (res?.error) throw new Error(res.message);
      message.success("Evento individual guardado en la hoja de vida del elemento");
      mutateLinks();
    } catch (err: any) {
      message.error(err.message || "Error al registrar evento");
      throw err;
    }
  };

  const handleAddMemberToGroup = async () => {
    try {
      const values = await addMemberForm.validateFields();
      setAddMemberLoading(true);

      // Create instance in storage and add to group
      const res = await securePost('/traceability/add-group-members', {
        company_uid: companyUid,
        group_id: linkedGroup.id,
        instance_ids: values.instance_ids,
        csrfToken: tokenData?.token?.uid,
      });

      if (res?.error) throw new Error(res.message);
      message.success("Miembros agregados al grupo");
      setAddMemberModalVisible(false);
      addMemberForm.resetFields();
      mutateLinks();
    } catch (err: any) {
      message.error(err.message || "Error al agregar miembros");
    } finally {
      setAddMemberLoading(false);
    }
  };

  const memberColumns = [
    {
      title: "Identificador (Chapeta / Serial)",
      key: "identifier",
      render: (_: any, record: any) => {
        const inst = record.traceable_instances;
        return (
          <Space direction="vertical" size={0}>
            <a
              style={{ fontWeight: 600, color: "#1890ff", fontSize: 14 }}
              onClick={() => {
                setViewingMember(inst);
                setMemberDrawerVisible(true);
              }}
            >
              {inst?.identifier || `ID #${record.instance_id}`}
            </a>
            {inst?.name && <Text type="secondary" style={{ fontSize: 12 }}>{inst.name}</Text>}
          </Space>
        );
      },
    },
    {
      title: "Raza / Categoría",
      key: "category",
      render: (_: any, record: any) => {
        const inst = record.traceable_instances;
        const meta = inst?.metadata || {};
        return (
          <Space direction="vertical" size={0}>
            <Tag color="blue">{inst?.module_options?.string_value || "Animal / Elemento"}</Tag>
            {meta.breed && <Text style={{ fontSize: 12 }}>Raza: {meta.breed}</Text>}
          </Space>
        );
      },
    },
    {
      title: "Peso / Métrica Actual",
      key: "metrics",
      render: (_: any, record: any) => {
        const meta = record.traceable_instances?.metadata || {};
        if (meta.weight_kg !== undefined) {
          return (
            <Tag color="cyan" style={{ fontSize: 13 }}>
              ⚖️ {meta.weight_kg} kg
            </Tag>
          );
        }
        if (meta.operating_hours !== undefined) {
          return <Tag color="orange">⏱️ {meta.operating_hours} hrs</Tag>;
        }
        return <Text type="secondary">-</Text>;
      },
    },
    {
      title: "Estado Individual",
      key: "status",
      render: (_: any, record: any) => {
        const status = record.traceable_instances?.status || "ACTIVE";
        return <Tag color={status === "ACTIVE" || status === "IN_PRODUCTION" ? "green" : "orange"}>{status}</Tag>;
      },
    },
    {
      title: "Fecha Ingreso al Grupo",
      key: "joined_at",
      render: (_: any, record: any) => dayjs(record.joined_at).format("DD/MM/YYYY"),
    },
    {
      title: "Acciones",
      key: "actions",
      align: "right" as const,
      render: (_: any, record: any) => {
        const inst = record.traceable_instances;
        return (
          <Space size="small">
            <Tooltip title="Registrar Pesaje / Evento Individual">
              <Button
                size="small"
                type="primary"
                icon={<ExperimentOutlined />}
                style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
                onClick={() => handleOpenMemberLog(inst)}
              >
                Control
              </Button>
            </Tooltip>
            <Tooltip title="Ver Hoja de Vida">
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => {
                  setViewingMember(inst);
                  setMemberDrawerVisible(true);
                }}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  if (isLoading) {
    return <Card loading style={{ margin: "16px 0" }} />;
  }

  // Not Linked State
  if (!linkedGroup) {
    return (
      <Card style={{ margin: "16px 0", borderRadius: 8 }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div>
              <Title level={4}>Sin {terminology.groupLabel} Vinculado</Title>
              <Paragraph type="secondary" style={{ maxWidth: 500, margin: "0 auto 16px" }}>
                Este ciclo productivo no tiene un {terminology.groupLabel.toLowerCase()} asociado.
                Vincularlo le permite llevar la hoja de vida, control de {terminology.metricWeightLabel.toLowerCase()} y procedimientos individuales de cada {terminology.memberUnitSingleLabel.toLowerCase()}.
              </Paragraph>
            </div>
          }
        >
          <Button
            type="primary"
            icon={<LinkOutlined />}
            onClick={() => setLinkModalVisible(true)}
            size="large"
          >
            Vincular {terminology.groupLabel}
          </Button>
        </Empty>

        <Modal
          open={linkModalVisible}
          title={`Vincular ${terminology.groupLabel} al Ciclo`}
          onCancel={() => setLinkModalVisible(false)}
          onOk={handleLinkGroup}
          confirmLoading={linkingLoading}
          okText="Vincular al Ciclo"
          cancelText="Cancelar"
        >
          <div style={{ padding: "12px 0" }}>
            <Text style={{ display: "block", marginBottom: 8 }}>
              Seleccione el {terminology.groupLabel.toLowerCase()} registrado en Storage para asociarlo a <strong>{cycle.name}</strong>:
            </Text>
            <Select
              style={{ width: "100%" }}
              placeholder={`Seleccionar ${terminology.groupLabel.toLowerCase()}...`}
              value={selectedGroupIdToLink}
              onChange={setSelectedGroupIdToLink}
              showSearch
              optionFilterProp="children"
            >
              {availableGroups.map((g: any) => (
                <Option key={g.id} value={g.id}>
                  {g.name} ({g.active_members_count || 0} miembros activos) — {g.module_options?.string_value || "General"}
                </Option>
              ))}
            </Select>
          </div>
        </Modal>
      </Card>
    );
  }

  // Linked State with Member Table
  return (
    <div style={{ margin: "16px 0" }}>
      {/* Header Info Card */}
      <Card size="small" style={{ marginBottom: 16, backgroundColor: "#f9f0ff", borderColor: "#d3adf7", borderRadius: 8 }}>
        <Row gutter={16} align="middle">
          <Col xs={24} sm={8}>
            <Space align="center">
              <TeamOutlined style={{ fontSize: 24, color: "#722ed1" }} />
              <div>
                <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
                  {terminology.groupLabel.toUpperCase()} VINCULADO
                </Text>
                <Title level={5} style={{ margin: 0, color: "#531dab" }}>
                  {linkedGroup.name}
                </Title>
              </div>
            </Space>
          </Col>
          <Col xs={12} sm={5}>
            <Statistic
              title="Miembros en el Grupo"
              value={activeMembers.length}
              valueStyle={{ color: "#722ed1", fontWeight: "bold" }}
              prefix={<DeploymentUnitOutlined />}
            />
          </Col>
          <Col xs={12} sm={5}>
            <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
              CATEGORÍA
            </Text>
            <Tag color="purple">{linkedGroup.module_options?.string_value || "General"}</Tag>
          </Col>
          <Col xs={24} sm={6} style={{ textAlign: "right" }}>
            <Space>
              <Popconfirm
                title={`¿Desvincular ${terminology.groupLabel.toLowerCase()} de este ciclo?`}
                description="El grupo y sus miembros seguirán existiendo en el sistema de trazabilidad."
                onConfirm={handleUnlinkGroup}
                okText="Sí, desvincular"
                cancelText="Cancelar"
              >
                <Button size="small" danger icon={<DisconnectOutlined />}>
                  Desvincular
                </Button>
              </Popconfirm>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Member List Table */}
      <Card
        title={
          <Space>
            <DeploymentUnitOutlined style={{ color: "#1890ff" }} />
            <span>{terminology.groupMembersLabel} ({activeMembers.length})</span>
          </Space>
        }
        extra={
          <Space>
            {selectedRowKeys.length > 0 && (
              <Button
                type="primary"
                icon={<MedicineBoxOutlined />}
                onClick={handleOpenBatchEvent}
              >
                Registrar Evento para {selectedRowKeys.length} Seleccionados
              </Button>
            )}
            <Button
              icon={<ExperimentOutlined />}
              style={{ backgroundColor: "#52c41a", borderColor: "#52c41a", color: "#fff" }}
              onClick={handleOpenBatchWeighing}
              disabled={activeMembers.length === 0}
            >
              {terminology.batchActionLabel}
            </Button>
          </Space>
        }
        style={{ borderRadius: 8 }}
      >
        {selectedRowKeys.length > 0 && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={
              <span>
                <strong>{selectedRowKeys.length}</strong> de {activeMembers.length} miembros seleccionados.
                <a onClick={() => setSelectedRowKeys(activeMembers.map((m: any) => m.id))} style={{ marginLeft: 8 }}>
                  Seleccionar todos
                </a>
                <a onClick={() => setSelectedRowKeys([])} style={{ marginLeft: 8 }}>
                  Limpiar selección
                </a>
              </span>
            }
          />
        )}
        <Table
          dataSource={activeMembers}
          columns={memberColumns}
          rowKey="id"
          size="middle"
          pagination={{ pageSize: 10, showSizeChanger: true }}
          locale={{ emptyText: "No hay miembros activos registrados en este grupo" }}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
          }}
        />
      </Card>

      {/* Modal: Batch Weighing / Inspection Spreadsheet */}
      <Modal
        open={batchWeighingVisible}
        title={
          <Space>
            <ExperimentOutlined style={{ color: "#52c41a", fontSize: 20 }} />
            <span>{terminology.batchModalTitle} ({activeMembers.length} {terminology.memberUnitLabel.toLowerCase()})</span>
          </Space>
        }
        onCancel={handleCloseBatchWeighing}
        onOk={handleFinishBatchWeighing}
        confirmLoading={batchLoading}
        width={920}
        okText="Finalizar y Guardar Jornada"
        cancelText="Cerrar"
        destroyOnHidden
      >
        {/* Real-time sync feedback banner */}
        <Alert
          type="info"
          showIcon
          icon={<ThunderboltOutlined style={{ color: "#1890ff" }} />}
          style={{ marginBottom: 12 }}
          message={
            <span>
              <strong>Guardado instantáneo activado:</strong> Cada {terminology.metricWeightLabel.toLowerCase()} se guarda automáticamente en la base de datos al presionar <strong>Enter</strong>, <strong>Tab</strong> o cambiar de casilla.
            </span>
          }
        />

        <Form form={batchWeighingForm} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="log_date" label="Fecha del Registro" rules={[{ required: true }]}>
                <DatePicker showTime style={{ width: "100%" }} format="YYYY-MM-DD HH:mm" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="title" label="Título del Registro">
                <Input placeholder={terminology.batchNotesPlaceholder} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="cost" label="Costo Operativo / Mano de Obra ($)" tooltip="Opcional: Si se contrató personal, servicios o técnicos">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  placeholder="0"
                  formatter={(value) => `${value}`.replaceAll(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => (value ? value.replaceAll(/\$\s?|(,*)/g, "") : "") as any}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="notes" label="Observaciones de la Jornada">
                <Input placeholder="Condiciones del lote, incidencias, clima, etc." />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ margin: "12px 0" }}>
            <Text strong style={{ fontSize: 13, color: "#1890ff" }}>
              📊 {terminology.batchSheetTitle}
            </Text>
          </Divider>

          {/* Progress and Summary Bar */}
          <Card size="small" style={{ marginBottom: 12, backgroundColor: "#fafafa" }}>
            <Row gutter={[12, 12]} align="middle">
              <Col xs={24} sm={10}>
                <Space direction="vertical" size={2} style={{ width: "100%" }}>
                  <Space style={{ justifyContent: "space-between", width: "100%" }}>
                    <Text strong style={{ fontSize: 12 }}>
                      Progreso de la Jornada:
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {Object.values(rowStatuses).filter((s) => s.status === "saved").length} de {activeMembers.length} ({activeMembers.length > 0 ? Math.round((Object.values(rowStatuses).filter((s) => s.status === "saved").length / activeMembers.length) * 100) : 0}%)
                    </Text>
                  </Space>
                  <Progress
                    percent={activeMembers.length > 0 ? Math.round((Object.values(rowStatuses).filter((s) => s.status === "saved").length / activeMembers.length) * 100) : 0}
                    status={Object.values(rowStatuses).filter((s) => s.status === "saved").length === activeMembers.length ? "success" : "active"}
                    strokeColor="#52c41a"
                    size="small"
                  />
                </Space>
              </Col>
              <Col xs={24} sm={14}>
                <Row gutter={8} align="middle" justify="end">
                  <Col xs={24} md={13}>
                    <Radio.Group
                      size="small"
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      buttonStyle="solid"
                    >
                      <Radio.Button value="ALL">Todos ({activeMembers.length})</Radio.Button>
                      <Radio.Button value="PENDING">
                        Pendientes ({activeMembers.length - Object.values(rowStatuses).filter((s) => s.status === "saved").length})
                      </Radio.Button>
                      <Radio.Button value="SAVED">
                        Guardados ({Object.values(rowStatuses).filter((s) => s.status === "saved").length})
                      </Radio.Button>
                    </Radio.Group>
                  </Col>
                  <Col xs={24} md={11}>
                    <Input
                      size="small"
                      placeholder="Buscar por código / nombre..."
                      prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      allowClear
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Card>

          <Table
            dataSource={activeMembers.filter((m: any) => {
              const instId = m.instance_id || m.traceable_instances?.id || m.id;
              const isSaved = rowStatuses[instId]?.status === "saved";

              if (filterStatus === "PENDING" && isSaved) return false;
              if (filterStatus === "SAVED" && !isSaved) return false;

              if (searchText.trim()) {
                const q = searchText.trim().toLowerCase();
                const ident = (m.traceable_instances?.identifier || "").toLowerCase();
                const name = (m.traceable_instances?.name || "").toLowerCase();
                if (!ident.includes(q) && !name.includes(q)) return false;
              }

              return true;
            })}
            rowKey={(record: any) => record.instance_id || record.traceable_instances?.id || record.id}
            size="small"
            pagination={false}
            scroll={{ y: 340 }}
            columns={[
              {
                title: "Identificador / Código",
                key: "identifier",
                width: 170,
                render: (_: any, record: any) => {
                  const instId = record.instance_id || record.traceable_instances?.id || record.id;
                  const isSaved = rowStatuses[instId]?.status === "saved";
                  return (
                    <Space direction="vertical" size={0}>
                      <Space size={4}>
                        <Text strong style={{ color: "#1890ff" }}>
                          {record.traceable_instances?.identifier}
                        </Text>
                        {isSaved && <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 13 }} />}
                      </Space>
                      {record.traceable_instances?.name && (
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {record.traceable_instances.name}
                        </Text>
                      )}
                    </Space>
                  );
                },
              },
              {
                title: `${terminology.metricWeightLabel} Anterior`,
                key: "prev_weight",
                align: "center" as const,
                width: 120,
                render: (_: any, record: any) => {
                  const prevW = record.traceable_instances?.metadata?.weight_kg;
                  const lastWeighed = record.traceable_instances?.metadata?.last_weighed_at;
                  return prevW !== undefined ? (
                    <Space direction="vertical" size={0}>
                      <Tag color="blue">{prevW} kg</Tag>
                      {lastWeighed && (
                        <Text type="secondary" style={{ fontSize: 10 }}>
                          {dayjs(lastWeighed).format("DD/MM")}
                        </Text>
                      )}
                    </Space>
                  ) : (
                    <Text type="secondary">-</Text>
                  );
                },
              },
              {
                title: `Nuevo ${terminology.metricWeightLabel}`,
                key: "new_weight",
                width: 150,
                render: (_: any, record: any) => {
                  const instId = record.instance_id || record.traceable_instances?.id || record.id;
                  const prevW = record.traceable_instances?.metadata?.weight_kg;
                  const val = batchWeights[instId];
                  return (
                    <InputNumber
                      min={0}
                      step={0.5}
                      style={{ width: "100%" }}
                      placeholder={prevW !== undefined ? `Ej. ${prevW}` : "Ej. 350"}
                      value={val}
                      onChange={(newVal) => {
                        setBatchWeights((prev) => ({
                          ...prev,
                          [instId]: newVal !== null && newVal !== undefined ? newVal : undefined,
                        }));
                      }}
                      onBlur={() => handleSaveSingleWeight(record)}
                      onPressEnter={() => handleSaveSingleWeight(record)}
                    />
                  );
                },
              },
              {
                title: "Variación / Ganancia",
                key: "diff",
                align: "center" as const,
                width: 120,
                render: (_: any, record: any) => {
                  const instId = record.instance_id || record.traceable_instances?.id || record.id;
                  const prevW = record.traceable_instances?.metadata?.weight_kg;
                  const newW = batchWeights[instId];
                  if (prevW !== undefined && newW !== undefined && newW > 0) {
                    const diff = Number((newW - prevW).toFixed(2));
                    return (
                      <Tag color={diff >= 0 ? "green" : "red"} style={{ fontWeight: "bold" }}>
                        {diff >= 0 ? `+${diff}` : diff} kg
                      </Tag>
                    );
                  }
                  return <Text type="secondary">-</Text>;
                },
              },
              {
                title: "Estado de Guardado",
                key: "status",
                align: "center" as const,
                width: 150,
                render: (_: any, record: any) => {
                  const instId = record.instance_id || record.traceable_instances?.id || record.id;
                  const statusObj = rowStatuses[instId];

                  if (statusObj?.status === "saving") {
                    return (
                      <Tag icon={<SyncOutlined spin />} color="processing">
                        Guardando...
                      </Tag>
                    );
                  }
                  if (statusObj?.status === "saved") {
                    return (
                      <Tooltip title={`Guardado en base de datos a las ${statusObj.savedAt || ""}`}>
                        <Tag icon={<CheckCircleOutlined />} color="success">
                          Guardado {statusObj.savedAt ? `(${statusObj.savedAt})` : ""}
                        </Tag>
                      </Tooltip>
                    );
                  }
                  if (statusObj?.status === "invalid") {
                    return (
                      <Tooltip title={statusObj.errorMsg || `Debe ingresar un ${terminology.metricWeightLabel.toLowerCase()} mayor a 0`}>
                        <Tag icon={<ExclamationCircleOutlined />} color="warning">
                          Inválido (&gt;0)
                        </Tag>
                      </Tooltip>
                    );
                  }
                  if (statusObj?.status === "error") {
                    return (
                      <Space size={2}>
                        <Tag icon={<CloseCircleOutlined />} color="error">
                          Error
                        </Tag>
                        <Button
                          size="small"
                          type="link"
                          style={{ padding: 0 }}
                          onClick={() => handleSaveSingleWeight(record)}
                        >
                          Reintentar
                        </Button>
                      </Space>
                    );
                  }
                  return <Tag color="default">⚪ Pendiente</Tag>;
                },
              },
              {
                title: "",
                key: "quick_save",
                align: "center" as const,
                width: 50,
                render: (_: any, record: any) => {
                  const instId = record.instance_id || record.traceable_instances?.id || record.id;
                  const statusObj = rowStatuses[instId];
                  return (
                    <Tooltip title="Guardar peso inmediatamente">
                      <Button
                        size="small"
                        type="text"
                        icon={<SaveOutlined style={{ color: statusObj?.status === "saved" ? "#52c41a" : "#1890ff" }} />}
                        onClick={() => handleSaveSingleWeight(record)}
                      />
                    </Tooltip>
                  );
                },
              },
            ]}
          />
        </Form>
      </Modal>

      {/* Modal: Batch Event for Selected Members */}
      <UnifiedTraceableLogModal
        visible={batchEventVisible}
        onClose={() => setBatchEventVisible(false)}
        instances={selectedInstances.map((m: any) => m.traceable_instances).filter(Boolean)}
        referenceType="CYCLE_MEMBER_LOG"
        referenceId={cycle.id}
        terminologyPreset={cycle?.productive_unit_type}
        onSubmit={handleSaveBatchEvent}
      />

      {/* Modal: Individual Log Form */}
      <UnifiedTraceableLogModal
        visible={memberLogModalVisible}
        onClose={() => setMemberLogModalVisible(false)}
        instance={selectedMember}
        referenceType="CYCLE_MEMBER_LOG"
        referenceId={cycle.id}
        terminologyPreset={cycle?.productive_unit_type}
        onSubmit={handleSaveMemberLog}
      />

      {/* Drawer: Individual Member Technical Sheet & History */}
      <Drawer
        open={memberDrawerVisible}
        onClose={() => setMemberDrawerVisible(false)}
        width={600}
        title={
          <Space>
            <BarcodeOutlined style={{ color: "#1890ff" }} />
            <span>Ficha de Elemento: {viewingMember?.identifier}</span>
          </Space>
        }
      >
        {viewingMember && (
          <div>
            <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Identificador">{viewingMember.identifier}</Descriptions.Item>
              <Descriptions.Item label="Nombre / Alias">{viewingMember.name || "-"}</Descriptions.Item>
              <Descriptions.Item label="Estado">{viewingMember.status}</Descriptions.Item>
              <Descriptions.Item label="Ubicación">{viewingMember.location || "-"}</Descriptions.Item>
              <Descriptions.Item label="Fecha Ingreso">
                {viewingMember.entry_date ? dayjs(viewingMember.entry_date).format("DD/MM/YYYY") : "-"}
              </Descriptions.Item>
            </Descriptions>

            {viewingMember.metadata && Object.keys(viewingMember.metadata).length > 0 && (
              <>
                <Divider orientation="left">Atributos</Divider>
                <Descriptions bordered size="small" column={2}>
                  {Object.entries(viewingMember.metadata).map(([k, v]) => (
                    <Descriptions.Item key={k} label={k}>
                      {typeof v === "number" ? v.toLocaleString() : String(v)}
                    </Descriptions.Item>
                  ))}
                </Descriptions>
              </>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default CycleTraceabilityMembers;
